import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Skeleton } from "@/components/ui/skeleton";
import { dashboardEChartsStore } from "@/features/esg-dashboard-v3/stores/e-charts.store";
import { dashboardEChartsTotalsStore } from "@/features/esg-dashboard-v3/stores/e-charts-totals.store";
import { formatValue } from "@/features/esg-dashboard-v3/domains/formatting";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildReportingPeriodLabel,
  buildTotalsChartData,
} from "./emissions-totals-chart.utils";

const ChartSkeleton = () => <Skeleton className="h-[370px] w-full" />;

const formatYAxis = (value) => {
  if (value === 0) return "0";
  return `${value / 1000}K`;
};

const CHART_MARGIN = { top: 10, right: 10, left: 8, bottom: 0 };
const Y_AXIS_WIDTH = 64;

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const total = payload.reduce((sum, item) => sum + Number(item.value), 0);

  return (
    <div className="rounded-xl border bg-card p-4 shadow-xl">
      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
        {`Year ${label}`}
      </p>

      <div className="space-y-2 text-xs">
        {payload.map((item) => (
          <div
            key={item.dataKey}
            className="flex items-center justify-between gap-8"
          >
            <span className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.name}
            </span>

            <span className="font-semibold">{`${formatValue(item.value)} tCO2e`}</span>
          </div>
        ))}

        <div className="mt-2 flex justify-between border-t pt-2 font-bold">
          <span>Total</span>
          <span>{`${formatValue(total)} tCO2e`}</span>
        </div>
      </div>
    </div>
  );
};

const TOOLTIP_CURSOR_STYLE = {
  fill: "hsl(var(--muted))",
};

const StackedEmissionsCard = observer(({ theme = "light" }) => {
  const totalsStore = dashboardEChartsTotalsStore;
  const chartsStore = dashboardEChartsStore;
  const activeYears = chartsStore.activeYears;
  const isFirstLoad = !totalsStore.hasLoadedOnce;

  const data = useMemo(
    () => buildTotalsChartData(totalsStore.summary, activeYears),
    [totalsStore.summary, activeYears],
  );
  const reportingPeriodLabel = useMemo(
    () => buildReportingPeriodLabel(activeYears),
    [activeYears],
  );

  const colors = {
    scope1: "#1e40af",
    scope2: "#3b82f6",
    scope3: "#93c5fd",
    grid: theme === "light" ? "#e2e8f0" : "#334155",
    text: theme === "light" ? "#64748b" : "#94a3b8",
  };

  return (
    <div className="rounded-md border bg-card/95 p-6 transition-all sm:p-8">
      <div className="mb-6">
        <h3 className="text-[17px] text-base font-bold">
          Total emissions by year, stacked by scope
        </h3>

        <p className="mt-1 text-xs text-muted-foreground">
          {`Company-wide Scope 1 + Scope 2 + Scope 3 stacked per ${reportingPeriodLabel} · tCO2e`}
        </p>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border bg-card px-4 py-3 text-xs font-semibold">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          Scope Colors
        </span>

        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.scope1 }}
          />
          Scope 1
        </div>

        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.scope2 }}
          />
          Scope 2
        </div>

        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.scope3 }}
          />
          Scope 3
        </div>
      </div>

      <div className="h-[360px] w-full">
        {isFirstLoad ? (
          <ChartSkeleton />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barSize={42} margin={CHART_MARGIN}>
              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                stroke={colors.grid}
              />

              <XAxis
                dataKey="year"
                tickLine={false}
                axisLine={false}
                stroke={colors.text}
                dy={12}
                style={{ fontSize: 11, fontWeight: 600 }}
              />

              <YAxis
                width={Y_AXIS_WIDTH}
                tickFormatter={formatYAxis}
                tickLine={false}
                axisLine={false}
                stroke={colors.text}
                style={{ fontSize: 11, fontWeight: 600 }}
              />

              <Tooltip
                content={<CustomBarTooltip />}
                cursor={TOOLTIP_CURSOR_STYLE}
              />

              <Bar
                dataKey="scope1"
                name="Scope 1"
                stackId="total"
                fill={colors.scope1}
              />

              <Bar
                dataKey="scope2"
                name="Scope 2"
                stackId="total"
                fill={colors.scope2}
              />

              <Bar
                dataKey="scope3"
                name="Scope 3"
                stackId="total"
                fill={colors.scope3}
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
});

export default StackedEmissionsCard;
